import React from 'react'
import { Button, Form, Input, Modal, Popconfirm, Table } from 'antd'
import { DeleteFilled, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import noProductImage from 'assets/images/no-image-product.png'
import { servicesApi } from 'api/services'
import { IServiceCategory } from 'models/services'
import { Uploader } from 'components/ui/Uploader/Uploader'
import { IMAGES_URL } from 'shared/constants/urls'
import { showServerError } from 'shared/utils/showServerError'

interface PoloClubCategoriesProps {
  // slug лендинг-страницы: категории скоупятся на подпроект (напр. ПолоКлуб)
  pageSlug: string
}

export const PoloClubCategories: React.FC<PoloClubCategoriesProps> = ({
  pageSlug
}) => {
  const [categories, setCategories] = React.useState<IServiceCategory[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [editingCategory, setEditingCategory] =
    React.useState<IServiceCategory | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [form] = Form.useForm()

  const getCategories = async () => {
    try {
      setIsLoading(true)
      const response = await servicesApi.getServiceCategories(pageSlug)
      setCategories(response.data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getCategories()
  }, [pageSlug])

  const openCreate = () => {
    setEditingCategory(null)
    setIsModalOpen(true)
  }

  const openEdit = (category: IServiceCategory) => {
    setEditingCategory(category)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingCategory(null)
    form.resetFields()
  }

  const handleDelete = async (id: string) => {
    try {
      await servicesApi.deleteServiceCategory(id)
      await getCategories()
    } catch (e) {
      showServerError(e)
    }
  }

  const handleSubmit = async (values: any) => {
    try {
      setIsSubmitting(true)
      // фото есть всегда, но при редактировании это может быть уже сохранённое —
      // тогда originFileObj пуст и картинку не трогаем
      const photo = values.photo?.[0]?.originFileObj
      if (editingCategory) {
        await servicesApi.updateServiceCategory(editingCategory._id, {
          name: values.name,
          photo
        })
      } else {
        await servicesApi.createServiceCategory({
          name: values.name,
          photo,
          page: pageSlug
        })
      }
      closeModal()
      await getCategories()
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  const columns: ColumnsType<IServiceCategory> = [
    {
      title: 'Фото',
      dataIndex: 'photo',
      width: 100,
      render: (_, { photo }) =>
        photo ? (
          <div
            style={{
              width: 80,
              height: 56,
              backgroundImage: `url(${IMAGES_URL}${photo})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              borderRadius: 4
            }}
          />
        ) : (
          <img src={noProductImage} alt='Категория' width={80} height={56} />
        )
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '',
      dataIndex: 'operation',
      width: 100,
      render: (_, category) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            icon={<EditOutlined />}
            size='small'
            onClick={() => openEdit(category)}
          />
          <Popconfirm
            title='Удалить категорию?'
            onConfirm={() => handleDelete(category._id)}
          >
            <Button type='primary' danger icon={<DeleteFilled />} size='small' />
          </Popconfirm>
        </div>
      )
    }
  ]

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16
        }}
      >
        <Button
          type='primary'
          size='large'
          icon={<PlusOutlined />}
          onClick={openCreate}
        >
          Добавить категорию
        </Button>
      </div>

      <Table
        scroll={{ x: 500 }}
        pagination={false}
        columns={columns}
        dataSource={categories}
        loading={isLoading}
        rowKey={(el) => el._id}
        rowClassName='tableRow'
      />

      <Modal
        title={editingCategory ? 'Изменить категорию' : 'Добавить категорию'}
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout='vertical'
          onFinish={handleSubmit}
          initialValues={
            editingCategory
              ? {
                name: editingCategory.name,
                // текущее фото как уже загруженный элемент — не требует повторной загрузки
                photo: editingCategory.photo
                  ? [
                    {
                      uid: '-1',
                      name: 'current-category',
                      status: 'done',
                      url: `${IMAGES_URL}${editingCategory.photo}`,
                      thumbUrl: `${IMAGES_URL}${editingCategory.photo}`
                    }
                  ]
                  : []
              }
              : {}
          }
        >
          <Uploader
            name='photo'
            rules={[{ required: true, message: 'Добавьте изображение' }]}
            type='variation'
            hint='Фото категории'
          />

          <Form.Item
            label='Название'
            name='name'
            rules={[{ required: true, message: 'Укажите название' }]}
          >
            <Input placeholder='Название категории' />
          </Form.Item>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 8
            }}
          >
            <Button onClick={closeModal}>Отмена</Button>
            <Button type='primary' htmlType='submit' loading={isSubmitting}>
              {editingCategory ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  )
}
